import React, { useState, useEffect } from "react";
import AiSensyLogo from "../../../../static/AiSensyLogo.svg";
import { resolveCountry, countryOptionsFor, phoneErrorFor } from "./dialCode";
import CkSelect from "./CkSelect";
import checkoutPageApi, {
  forgetLeadToken,
} from "../../../../services/checkoutPageApi";
import { countries } from "../../../PaymentPages/countryData";
import useRazorpayCheckout from "../../hooks/useRazorpayCheckout";
import useTracking from "../../../../tracking/useTracking";
import {
  Container,
  Grid,
  Button,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import useCheckoutClassicStyles from "./checkoutClassicStyles";
import CheckoutHero from "./CheckoutHero";
import BenefitsList from "./BenefitsList";
import LeadForm from "./LeadForm";
import PlanCard from "./PlanCard";
import OrderSummary from "./OrderSummary";
import SuccessScreen from "./SuccessScreen";
import SupportCard from "./SupportCard";
import MobileStickyBar from "./MobileStickyBar";

const DEFAULTS = {
  heroTitle: "Your First Step to 5x Business Engagement with AI",
  sidebarTitle: "Why Choose AiSensy for WhatsApp Business?",
  formTitle: "Tell Us About Your Business",
  formCtaLabel: "Continue to Payment",
  mobileCtaLabel: "Continue",
  plansTitle: "Limited Time Offer Plan!",
  summaryCtaLabel: "Proceed to Pay",
};

const symbolFor = (currency) => {
  if (currency === "INR") return "₹";
  if (currency === "AED") return "AED ";
  if (currency === "USD") return "$";
  // Never render a bare number on a page that quotes a price — an unrecognised
  // currency shows its code rather than nothing at all.
  return currency ? `${currency} ` : "";
};

const money = (sym, n) =>
  `${sym}${
    Number.isFinite(+n)
      ? Number(n).toLocaleString("en-IN", {
          // Tax and totals carry paise precision (e.g. GST ₹508.14), so always
          // show 2 decimals rather than a bare, inconsistently-formatted number.
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "0.00"
  }`;

// Billing period text for the price caption, e.g. "(Billed per month)".
const BILLED = {
  monthly: "per month",
  quarterly: "every 3 months",
  yearly: "per year",
};

// Line items with formatted prices, for the plan card and the order summary.
const lineItemRows = (cp, sym) =>
  (cp.lineItems || []).map((li) => ({
    id: li.id,
    title: li.title || li.id,
    amountLabel: money(sym, li.amount),
    originalLabel:
      li.originalAmount != null && li.originalAmount > li.amount
        ? money(sym, li.originalAmount)
        : null,
    // A missing `recurring` counts as recurring.
    oneTime: li.recurring === false,
  }));

// Caption under the plan price. If the plan has one-time items, shows the renewal price.
const billingCaption = (cp, sym) => {
  const items = cp.lineItems || [];
  const oneTime = items.filter((li) => li.recurring === false);
  if (items.length > 0 && oneTime.length === items.length) return "(One-time payment)";
  const period = BILLED[cp.duration];
  if (!period) return null;
  if (oneTime.length === 0) return `(Billed ${period})`;
  const renewal = items
    .filter((li) => li.recurring !== false)
    .reduce((sum, li) => sum + Number(li.amount || 0), 0);
  return `Renews at ${money(sym, renewal)} ${period}`;
};

// The canvas renders Name / Email / Phone as fixed fields ahead of the
// configured list, then filters them out of it. Mirroring that keeps field
// order identical between the builder and this page.
const FIXED_FIELD_KEYS = ["fullName", "emailAddress", "phoneNumber"];

// Per-page checkout session, so Back/refresh reuse the same lead instead of
// creating duplicates. Scoped by pageId; cleared on success/expiry.
const sessionKey = (pageId) => `checkout_lead_${pageId}`;

const readSession = (pageId) => {
  try {
    const raw = sessionStorage.getItem(sessionKey(pageId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeSession = (pageId, value) => {
  try {
    sessionStorage.setItem(sessionKey(pageId), JSON.stringify(value));
  } catch {
    /* private-mode / quota — reuse simply falls back to a fresh lead */
  }
};

const clearSession = (pageId) => {
  try {
    sessionStorage.removeItem(sessionKey(pageId));
  } catch {
    /* ignore */
  }
};

// Stable serialization of the submitted payload, so we can tell whether the buyer
// actually edited the form between Continue presses.
const fingerprintOf = (data) =>
  JSON.stringify(
    Object.keys(data)
      .sort()
      .map((k) => [k, data[k]])
  );


const scrollToFirstError = (errors) => {
  const invalid = Array.from(document.querySelectorAll("[data-field]")).find(
    (el) => errors[el.getAttribute("data-field")]
  );
  if (invalid) invalid.scrollIntoView({ behavior: "smooth", block: "center" });
};

const scrollToErrorAnchor = () => {
  const anchor = document.querySelector("[data-error-anchor]");
  if (anchor) anchor.scrollIntoView({ behavior: "smooth", block: "center" });
};

/**
 * Buyer-facing "classic" template. Presentational only: it renders the page
 * exactly as authored, but submits nothing. Lead creation, coupons, gateway
 * handoff and verification are a separate follow-up project.
 *
 * Markup mirrors the console's FormStepCanvas / PlansStepCanvas / OrderSummary
 * class-for-class, minus the editing affordances. Styling uses Material UI
 * (makeStyles) — the old checkout-classic.css has been removed.
 */
export default function ClassicTemplate({ page, config, products, pageId, accessToken, affiliate }) {
  // Restore any in-flight session for this page (survives Back and refresh).
  const restored = React.useMemo(() => readSession(pageId), [pageId]);
  const [step, setStep] = useState(restored?.step ?? 0);
  const classes = useCheckoutClassicStyles();
  const isMobile = useMediaQuery(useTheme().breakpoints.down("sm"));
  // Single source of truth for every input on the form step, keyed by field key
  // (fullName / email / phone are the fixed fields; the rest come from config).
  const [formValues, setFormValues] = useState(restored?.formValues ?? {});
  const setField = (key, value) =>
    setFormValues((prev) => ({ ...prev, [key]: value }));
  // Fingerprint of the form payload the current leadId was created/updated with.
  const leadFingerprintRef = React.useRef(restored?.fingerprint ?? null);
  const [leadId, setLeadId] = useState(restored?.leadId ?? null);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [serverSummary, setServerSummary] = useState(null);
  // No-coupon server totals, fetched on load. Keeps the displayed total paise-exact and is the fallback
  // when a coupon is removed or fails.
  const [baseSummary, setBaseSummary] = useState(null);
  const [couponError, setCouponError] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [openDropdownKey, setOpenDropdownKey] = useState(null);

  useEffect(() => {
    let blurTimer = null;
    const isTextInput = (el) =>
      !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA");

    const syncFromActiveElement = () => {
      if (blurTimer) {
        clearTimeout(blurTimer);
        blurTimer = null;
      }
      setKeyboardOpen(isTextInput(document.activeElement));
    };
    const onFocusIn = (e) => {
      if (!isTextInput(e.target)) return;
      if (blurTimer) {
        clearTimeout(blurTimer);
        blurTimer = null;
      }
      setKeyboardOpen(true);
    };
    const onFocusOut = (e) => {
      if (!isTextInput(e.target)) return;
      if (blurTimer) clearTimeout(blurTimer);
      // Deferred so field-to-field focus moves don't flash the bar back in.
      blurTimer = setTimeout(() => {
        blurTimer = null;
        setKeyboardOpen(isTextInput(document.activeElement));
      }, 150);
    };
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);

    document.addEventListener("pointerup", syncFromActiveElement, true);
    document.addEventListener("click", syncFromActiveElement, true);
    document.addEventListener("pointercancel", syncFromActiveElement, true);
    return () => {
      if (blurTimer) clearTimeout(blurTimer);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      document.removeEventListener("pointerup", syncFromActiveElement, true);
      document.removeEventListener("click", syncFromActiveElement, true);
      document.removeEventListener("pointercancel", syncFromActiveElement, true);
    };
  }, []);

  const handleDropdownOpenChange = React.useCallback((key, isOpen) => {
    setOpenDropdownKey((prev) => (isOpen ? key : prev === key ? null : prev));
  }, []);

  useEffect(() => {
    setKeyboardOpen(false);
    setOpenDropdownKey(null);
  }, [step]);

  const barSuppressed = keyboardOpen || openDropdownKey !== null;

  useEffect(() => {
    if (formError) scrollToErrorAnchor();
  }, [formError, step]);

  useEffect(() => {
    if (payError) scrollToErrorAnchor();
  }, [payError]);

  const { open } = useRazorpayCheckout();
  // Loads the page's ad pixels and sends one PageView.
  const track = useTracking(page && page.tracking && page.tracking.pixels);
  useEffect(() => {
    track.emit("PageView");
  }, [track]);
  // Keep the stored session in sync so Back/refresh resume the same lead.
  useEffect(() => {
    if (!pageId) return;
    if (!leadId) return; // nothing worth restoring until a lead exists
    writeSession(pageId, {
      leadId,
      step,
      formValues,
      fingerprint: leadFingerprintRef.current,
    });
  }, [pageId, leadId, step, formValues]);
  // The picker's chosen country per phone field. Nothing is submitted from this
  // page yet — this only drives what the prefix displays.
  const [phoneCountries, setPhoneCountries] = useState({});

  const slotOverrides =
    (page && page.presentation && page.presentation.slotOverrides) || {};
  const pg = slotOverrides.page || {};
  const offerOverrides = slotOverrides.offerOverrides || {};
  const cob = pg.cobranding || {};
  const benefits = Array.isArray(pg.benefits) ? pg.benefits : [];
  const region = (config && config.region) || {};
  // Coupon UI mode resolved server-side by getPublicPage: "input" (an active
  // coupon exists — show the box), "pre_applied" (a signed-link coupon is
  // already applied — hide the box), or "none" (no coupons — no UI).
  const couponMode = (config && config.coupon && config.coupon.mode) || "none";

  // Always pull the server summary on load so the displayed total is paise-exact and
  // matches what the gateway will charge (client GST rounding otherwise drifts). With a
  // "pre_applied" signed-link coupon this baseline already carries the discount; for
  // "input"/"none" it's the no-coupon total. Applying a coupon overrides via serverSummary.
  useEffect(() => {
    let cancelled = false;
    checkoutPageApi
      .getSummary(pageId, undefined, accessToken)
      .then((s) => {
        if (!cancelled) setBaseSummary(s);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [pageId, accessToken]);
  const sym = symbolFor(region.currency);
  const gstPct = Number(region.gstPercentage || 0);
  const gstLabel = region.gstLabel || "GST";
  const formFields = (config && config.formFields) || [];
  const configProducts = (config && config.products) || [];
  const accent = pg.accentColor || "#04cf66";

  // The config's phone field (if any) drives the fixed Phone Number picker.
  const phoneField = formFields.find((f) => f.key === "phoneNumber") || null;

  // Resolves the dial code for a phone field from the picker's current choice,
  // falling back through the field/region default chain (same as dialCode.js).
  const dialCodeFor = (stateKey, field) => {
    const code = phoneCountries[stateKey] || resolveCountry(field, region).code;
    const c = countries.find((x) => x.code === code);
    return (c && c.dial_code) || region.defaultDialCode || "+91";
  };

  // Composes the payload the server validates. Phone values are prefixed with
  // the picker's dial code so the stored number is fully international.
  const buildFormData = () => {
    const data = {};
    formFields.forEach((f) => {
      const v = formValues[f.key];
      if (f.type === "phone" && v) {
        data[f.key] = `${dialCodeFor(f.key, f)}${v}`;
      } else {
        data[f.key] = v;
      }
    });
    return data;
  };

  // Validates email/phone on blur or country change. countryCode is passed because the
  // picker state hasn't updated yet. Empty values are checked on Continue.
  const handleFieldBlur = (key, countryCode) => {
    const v = formValues[key];
    if (!v) return;
    const f = formFields.find((x) => x.key === key) || (key === "phoneNumber" ? phoneField : null);
    let err = null;
    if (key === "phoneNumber" || (f && f.type === "phone")) {
      err = phoneErrorFor(
        countryCode || phoneCountries[key] || resolveCountry(f, region).code,
        v
      );
    } else if (key === "emailAddress" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      err = "Enter a valid email";
    } else {
      return;
    }
    setFieldErrors((prev) => ({ ...prev, [key]: err || undefined }));
  };

  const handleContinue = async () => {
    setFormError(null);
    // Light pre-check for obvious gaps to avoid an obviously-doomed round-trip.
    const preErrors = {};
    formFields.forEach((f) => {
      if (f.required && !formValues[f.key]) {
        preErrors[f.key] = `${f.label} is required`;
      } else if (f.type === "phone" && formValues[f.key]) {
        const code = phoneCountries[f.key] || resolveCountry(f, region).code;
        const phoneErr = phoneErrorFor(code, formValues[f.key]);
        if (phoneErr) preErrors[f.key] = phoneErr;
      }
    });
    if (
      formValues.emailAddress &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.emailAddress)
    ) {
      preErrors.emailAddress = "Enter a valid email";
    }
    if (Object.keys(preErrors).length) {
      setFieldErrors(preErrors);
      scrollToFirstError(preErrors);
      return;
    }
    setFieldErrors({});
    setSubmitting(true);
    const payload = buildFormData();
    const fingerprint = fingerprintOf(payload);
    try {
      if (leadId && fingerprint === leadFingerprintRef.current) {
        // Same lead, no edits since it was created — reuse it, no network call.
        setStep(1);
      } else if (leadId) {
        // Buyer edited the form on return — update the same lead in place.
        try {
          await checkoutPageApi.updateLeadCustomer(leadId, payload);
          leadFingerprintRef.current = fingerprint;
          setStep(1);
        } catch (err) {
          if (
            err.code === "INVALID_STATE" ||
            err.code === "LEAD_EXPIRED" ||
            // IDENTITY_LOCKED: the buyer reached the payment screen and has now come back to
            // change their email or phone. Those are frozen once an order exists, because the
            // coupon limit was validated against them. Start a fresh lead with the new
            // details instead — the buyer just sees Continue working normally.
            //
            // Deliberately NOT calling forgetLeadToken(leadId) here: a second tab may still
            // have the old Razorpay modal open, and it needs that token to verify if the
            // buyer completes payment there. sessionStorage is shared across tabs of this
            // origin, so deleting it would break that tab's /verify call.
            err.code === "IDENTITY_LOCKED"
          ) {
            // Stale or superseded lead — drop it and create a fresh one below.
            clearSession(pageId);
            setLeadId(null);
            leadFingerprintRef.current = null;
            const { leadId: id } = await checkoutPageApi.createLead(pageId, payload);
            leadFingerprintRef.current = fingerprint;
            setLeadId(id);
            if (id) track.emit("Lead", {}, id);
            setStep(1);
          } else {
            throw err;
          }
        }
      } else {
        // First time — create the lead.
        const { leadId: id } = await checkoutPageApi.createLead(pageId, payload);
        leadFingerprintRef.current = fingerprint;
        setLeadId(id);
        if (id) track.emit("Lead", {}, id);
        setStep(1);
      }
    } catch (err) {
      if (err.code === "VALIDATION_ERROR" && err.errors) {
        setFieldErrors(err.errors);
        scrollToFirstError(err.errors);
      } else {
        setFormError(err.message || "Could not continue. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const applyCoupon = async () => {
    setCouponError(null);
    setCouponLoading(true);
    try {
      const s = await checkoutPageApi.getSummary(
        pageId,
        couponInput.trim(),
        accessToken
      );
      setServerSummary(s);
      setAppliedCoupon(couponInput.trim());
    } catch (err) {
      setServerSummary(null);
      setAppliedCoupon(null);
      setCouponError(err.message || "Invalid coupon");
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    // Clear the coupon summary; display falls back to the no-coupon server baseline.
    setServerSummary(null);
    setAppliedCoupon(null);
    setCouponInput("");
    setCouponError(null);
  };

  const handlePay = async () => {
    setPayError(null);
    setPaying(true);
    try {
      const order = await checkoutPageApi.createOrder(
        leadId,
        appliedCoupon || undefined,
        accessToken
      );
      track.emit("InitiateCheckout", { value: order.amount, currency: order.currency }, leadId);
      await open({
        key: order.gatewayKey,
        orderId: order.gatewayOrderId,
        amount: order.amount,
        currency: order.currency,
        name: cob.enabled && cob.partnerName ? cob.partnerName : "AiSensy",
        prefill: {
          name: formValues.fullName || "",
          email: formValues.emailAddress || "",
          contact: formValues.phoneNumber
            ? `${dialCodeFor("phoneNumber", phoneField)}${formValues.phoneNumber}`
            : "",
        },
        onSuccess: async (resp) => {
          track.emit(
            "Purchase",
            { value: order.amount, currency: order.currency },
            resp.razorpay_order_id || order.gatewayOrderId
          );
          // payment is now confirmed server-side by the billing
          // webhook. We no longer call /verify here. Access details are delivered via
          // email/WhatsApp by the fulfillment worker. Kept for a future revival (e.g. a
          // synchronous fast-path or a custom destinationUrl redirect).
          //
          // try {
          //   const result = await checkoutPageApi.verifyPayment({
          //     leadId,
          //     gatewayOrderId: resp.razorpay_order_id,
          //     gatewayPaymentId: resp.razorpay_payment_id,
          //     gatewaySignature: resp.razorpay_signature,
          //   });
          //   // Payment done — this session is spent; the next purchase starts clean.
          //   clearSession(pageId);
          //   // Drop the spent lead credential too, so repeat purchases in the same tab
          //   // don't accumulate dead tokens in sessionStorage.
          //   forgetLeadToken(leadId);
          //   // Show the built-in screen when an access code was minted (the buyer
          //   // needs to see it); otherwise honour the configured destination.
          //   if (!result.accessCode && result.destinationUrl) {
          //     window.location.href = result.destinationUrl;
          //     return;
          //   }
          //   setSuccess(result);
          // } catch (err) {
          //   setPayError(
          //     err.message ||
          //       "Payment verification failed. If you were charged, contact support."
          //   );
          //   setPaying(false);
          // }

          // Razorpay's handler only fires on a genuine successful payment. Show the
          // built-in generic confirmation; nothing sensitive is granted client-side.
          clearSession(pageId);
          forgetLeadToken(leadId);
          setSuccess({ accessCode: null, destinationUrl: null });
        },
        // Buyer closed the modal without paying — a cancel, not a failure. The
        // lead stays payable; clicking Proceed to Pay again just retries.
        onDismiss: () => {
          checkoutPageApi.markPaymentAttemptEnded(
            leadId,
            order.gatewayOrderId,
            "cancelled"
          );
          setPayError("Payment cancelled — you can try again when you're ready.");
          setPaying(false);
        },
        // Genuine gateway decline. Still retryable server-side.
        onFailure: (gatewayErr) => {
          checkoutPageApi.markPaymentAttemptEnded(
            leadId,
            order.gatewayOrderId,
            "failed",
            gatewayErr
          );
          setPayError("Payment was not completed. You can try again.");
          setPaying(false);
        },
      });
    } catch (err) {
      if (err.code === "LEAD_EXPIRED") {
        clearSession(pageId);
        leadFingerprintRef.current = null;
        setFormError("Your session expired. Please start again.");
        setStep(0);
        setLeadId(null);
      } else {
        setPayError(err.message || "Could not start payment. Please try again.");
      }
      setPaying(false);
    }
  };

  const detailFor = (productId) =>
    (products || []).find((x) => String(x._id) === String(productId)) || {};

  const lines = configProducts.map((cp) => {
    const d = detailFor(cp.productId);
    return {
      name: d.planTitle || d.name || "Product",
      price: Number(cp.snapshotBasePrice || 0),
    };
  });
  const subtotal = lines.reduce((s, l) => s + l.price, 0);
  const gst = gstPct > 0 ? Math.round(subtotal * (gstPct / 100)) : 0;
  const total = subtotal + gst;

  // Server totals are authoritative: the applied-coupon summary wins, else the no-coupon
  // baseline fetched on load. Client math (subtotal/gst/total above) is only a fallback
  // for the brief moment before the baseline arrives (or if that fetch failed). Declared
  // after the totals it reads — const TDZ makes ordering load-bearing here.
  const authoritative = serverSummary || baseSummary;
  const display = authoritative
    ? {
        subtotal: authoritative.productAmount,
        discount: authoritative.discount,
        gst: authoritative.gstAmount,
        total: authoritative.totalAmount,
      }
    : { subtotal, discount: 0, gst, total };

  const renderCountrySelect = (field, stateKey) => {
    const selected = phoneCountries[stateKey] || resolveCountry(field, region).code;
    const options = countryOptionsFor(field, region).map((c) => ({
      value: c.code,
      // Closed trigger stays compact (flag + dial); the open row mirrors the
      // RegionalPlansPage reference (flag + name + dial), searchable by both.
      trigger: `${c.flag} ${c.dial_code}`,
      search: `${c.name || ""} ${c.dial_code}`,
      node: (
        <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span style={{ fontSize: 16, flex: "none" }}>{c.flag}</span>
          <span
            style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {c.name}
          </span>
          <span style={{ color: "#666", flex: "none" }}>{c.dial_code}</span>
        </span>
      ),
    }));
    return (
      <CkSelect
        variant="compact"
        bare
        searchable
        searchPlaceholder="Search country..."
        ariaLabel="Country code"
        value={selected}
        options={options}
        onChange={(v) => {
          setPhoneCountries((prev) => ({ ...prev, [stateKey]: v }));
          handleFieldBlur(stateKey, v);
        }}
        onOpenChange={(isOpen) =>
          handleDropdownOpenChange(`phone:${stateKey}`, isOpen)
        }
      />
    );
  };

  if (success) {
    const firstName = (formValues.fullName || "").trim().split(" ")[0] || "";
    return (
      <SuccessScreen
        firstName={firstName}
        affiliate={affiliate}
      />
    );
  }

  const configFields = formFields.filter((f) => !FIXED_FIELD_KEYS.includes(f.key));

  return (
    <div className={classes.root}>
      <Container className={classes.container} maxWidth="lg">
        {step === 0 ? (
          <Grid container spacing={4}>
            <Grid item xs={12} md={5}>
              <CheckoutHero
                logo={AiSensyLogo}
                cob={cob}
                title={pg.heroTitle || DEFAULTS.heroTitle}
              />
              {!isMobile && (
                <BenefitsList
                  title={pg.sidebarTitle || DEFAULTS.sidebarTitle}
                  benefits={benefits}
                />
              )}
            </Grid>
            <Grid item xs={12} md={7}>
              <LeadForm
                formTitle={pg.formTitle || DEFAULTS.formTitle}
                formValues={formValues}
                setField={setField}
                onFieldBlur={handleFieldBlur}
                fieldErrors={fieldErrors}
                formError={formError}
                configFields={configFields}
                phoneField={phoneField}
                renderCountrySelect={renderCountrySelect}
                submitting={submitting}
                onContinue={handleContinue}
                ctaLabel={pg.formCtaLabel || DEFAULTS.formCtaLabel}
                hideCta
                onDropdownOpenChange={handleDropdownOpenChange}
              />
              {isMobile && (
                <BenefitsList
                  title={pg.sidebarTitle || DEFAULTS.sidebarTitle}
                  benefits={benefits}
                />
              )}
              <SupportCard affiliate={affiliate} />
            </Grid>
          </Grid>
        ) : (
          <div>
            <CheckoutHero
              logo={AiSensyLogo}
              cob={cob}
              title={pg.plansTitle || DEFAULTS.plansTitle}
            />
            <Grid container spacing={4} style={{ marginTop: 12 }}>
              <Grid item xs={12} md={7}>
                {configProducts.map((cp) => {
                  const d = detailFor(cp.productId);
                  const ov = offerOverrides[String(cp.productId)] || {};
                  const feats = Array.isArray(ov.features)
                    ? ov.features
                    : Array.isArray(d.features)
                    ? d.features
                    : [];
                  return (
                    <PlanCard
                      key={String(cp.productId)}
                      headline={ov.headline || d.planTitle || d.name || "Product"}
                      badge={ov.badge}
                      subtitle={d.planSubtitle}
                      features={feats}
                      lineItems={lineItemRows(cp, sym)}
                      priceLabel={money(sym, cp.snapshotBasePrice)}
                      billedLabel={billingCaption(cp, sym)}
                      gstPct={gstPct}
                      gstLabel={gstLabel}
                    />
                  );
                })}
                <Button
                  className={classes.secondaryButton}
                  variant="outlined"
                  onClick={() => setStep(0)}
                >
                  Back
                </Button>
              </Grid>
              <Grid item xs={12} md={5}>
                <OrderSummary
                  showCoupon={couponMode === "input"}
                  couponInput={couponInput}
                  onCouponChange={setCouponInput}
                  onApplyCoupon={applyCoupon}
                  onRemoveCoupon={removeCoupon}
                  couponLoading={couponLoading}
                  couponError={couponError}
                  // One row per line item; a product without line items shows one row.
                  lines={configProducts.flatMap((cp, i) => {
                    const rows = lineItemRows(cp, sym);
                    if (rows.length === 0) {
                      return [{ name: lines[i].name, priceLabel: money(sym, lines[i].price) }];
                    }
                    return rows.map((r) => ({
                      name: r.title,
                      priceLabel: r.amountLabel,
                      originalLabel: r.originalLabel,
                      oneTime: r.oneTime,
                    }));
                  })}
                  subtotalLabel={money(sym, display.subtotal)}
                  discountLabel={
                    display.discount > 0 ? money(sym, display.discount) : null
                  }
                  appliedCoupon={appliedCoupon}
                  gstPct={gstPct}
                  gstLabel={gstLabel}
                  gstAmountLabel={money(sym, display.gst)}
                  totalLabel={money(sym, display.total)}
                  accent={accent}
                  paying={paying}
                  canPay={!!leadId}
                  onPay={handlePay}
                  payError={payError}
                  ctaLabel={pg.summaryCtaLabel || DEFAULTS.summaryCtaLabel}
                  hideCta
                />
                <SupportCard affiliate={affiliate} />
              </Grid>
            </Grid>
          </div>
        )}
        <div className={classes.mobileStickySpacer} />
      </Container>
      <MobileStickyBar
        suppressed={barSuppressed}
        {...(step === 0
          ? {
              label: pg.formCtaLabel || DEFAULTS.mobileCtaLabel,
              onClick: handleContinue,
              disabled: submitting,
              busy: submitting,
              trust: true,
            }
          : {
              label: pg.summaryCtaLabel || DEFAULTS.summaryCtaLabel,
              onClick: handlePay,
              disabled: paying || !leadId,
              busy: paying,
              totalLabel: money(sym, display.total),
              totalCaption: gstPct > 0 ? "incl. GST" : null,
            })}
      />
    </div>
  );
}
